import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { of as observableOf } from 'rxjs';

import { SearchService } from '../search.service';
import { SearchResult, FileSearchResult, UnifiedSearchResult } from '../searchResult';
import { environment } from 'src/environments/environment';

import { faFile, faFileExcel, faFileWord, faFilePdf, faEnvelope, faFileLines } from '@fortawesome/pro-light-svg-icons';
import { MatButtonModule } from '@angular/material/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { NgIf, NgFor, SlicePipe } from '@angular/common';

// One summary row shown beneath the CUB results: a source category with its result count and the
// facet-rail filter to pre-select when the row is clicked through to the full search page.
interface CategorySummary {
  label: string;
  filter: string;
  count: number;
  img: string;
  colorKey: string;
}

@Component({
  selector: 'cub-search-result-popup',
  templateUrl: './search-result-popup.component.html',
  styleUrls: ['./search-result-popup.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgIf, NgFor, SlicePipe, MatListModule, MatIconModule, FontAwesomeModule, MatButtonModule]
})
export class SearchResultPopupComponent implements OnInit, OnDestroy {

  show = false;
  cubSearchResults: SearchResult[];
  fileSearchResults: FileSearchResult[];
  spSearchResults: SearchResult[];
  isSearching: boolean;

  // Category summary rows (Property Files / Lease Files / Egnyte / SharePoint) and their load state.
  categories: CategorySummary[] = [];
  loadingCategories = false;

  // Index of the keyboard-highlighted row across the flat nav list (top-5 CUB rows followed by the
  // category rows). -1 means nothing is highlighted.
  selectedIndex = -1;

  // Position (anchored to the search box's left edge, measured when the popup opens).
  posLeft = 300;
  posTop = 55;
  posWidth = 430;

  faFile = faFile;
  faFileExcel = faFileExcel;
  faFileWord = faFileWord;
  faFilePdf = faFilePdf;
  faEnvelope = faEnvelope;
  faFileLines = faFileLines;

  private subs: Subscription[] = [];

  constructor(
    private searchService: SearchService,
    private router: Router
  ) {
    this.isSearching = false;
  }

  ngOnInit() {
    this.searchService.isSearching$.subscribe(result => {
      this.isSearching = result;
    });

    this.searchService.showSearchPopup$.subscribe(result => {
      if (this.router.url.indexOf('/search') > -1) {  // don't show popup if we are on search page
        this.show = false;
      } else {
        this.show = result;
        if (result) {
          this.positionUnderSearchBox();
        }
      }
    });

    this.searchService.searchResults$.subscribe(results => {
      this.cubSearchResults = this.sortCubByType(results);
      this.isSearching = false;
      this.selectedIndex = -1;
    });

    this.searchService.fileSearchResults$.subscribe(results => {
      this.fileSearchResults = results;
      this.isSearching = false;
    });

    // Egnyte + SharePoint category counts. Runs off the shared term stream so the summary rows
    // stay in sync with whatever CUB is showing; Egnyte paging is the slow part, so the counts
    // fill in a moment after the CUB list.
    this.subs.push(
      this.searchService.term$.pipe(
        debounceTime(450),
        distinctUntilChanged(),
        switchMap(term => {
          this.categories = [];
          this.selectedIndex = -1;
          if (!term || term.trim().length < 3) {
            this.loadingCategories = false;
            return observableOf([] as UnifiedSearchResult[]);
          }
          this.loadingCategories = true;
          return this.searchService.searchFileSources(term.trim());
        })
      ).subscribe(results => {
        this.loadingCategories = false;
        this.buildCategories(results);
      })
    );

    // Keyboard navigation raised from the toolbar search box: move the highlight or activate a row.
    this.subs.push(
      this.searchService.popupKeyNav$.subscribe(command => this.handleKeyNav(command))
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  // Preferred ordering of CUB result types in the quick-search popup. Anything not listed
  // (e.g. table matches typed "CUB") sorts after these, keeping its original relative order.
  private static readonly cubTypeOrder: string[] = ['Property', 'Lease', 'Investment', 'Dev', 'User'];

  private sortCubByType(results: SearchResult[]): SearchResult[] {
    const rank = (t: string) => {
      const i = SearchResultPopupComponent.cubTypeOrder.indexOf(t);
      return i === -1 ? SearchResultPopupComponent.cubTypeOrder.length : i;
    };
    // Stable sort (map to index, compare, fall back to original position) so items of the same
    // type keep the order the backend returned them in.
    return (results || [])
      .map((r, i) => ({ r, i }))
      .sort((a, b) => rank(a.r.type) - rank(b.r.type) || a.i - b.i)
      .map(x => x.r);
  }

  // The top CUB rows actually rendered in the popup (capped at 5, matching the template slice).
  get navCubResults(): SearchResult[] {
    return (this.cubSearchResults || []).slice(0, 5);
  }

  // Total number of keyboard-navigable rows (CUB rows + category rows + the "Search More" button,
  // which always sits at the end of the list).
  get navCount(): number {
    return this.navCubResults.length + this.categories.length + 1;
  }

  // Flat index of the trailing "Search More" button.
  get searchMoreIndex(): number {
    return this.navCubResults.length + this.categories.length;
  }

  // Moves the highlight (arrow keys, wrapping around) or activates the highlighted row (Enter).
  // With nothing highlighted, Enter falls through to the full search page.
  private handleKeyNav(command: 'up' | 'down' | 'enter') {
    if (!this.show) {
      return;
    }
    if (command === 'enter') {
      this.activateSelected();
      return;
    }
    const count = this.navCount;
    if (count === 0) {
      return;
    }
    if (command === 'down') {
      this.selectedIndex = this.selectedIndex < 0 ? 0 : (this.selectedIndex + 1) % count;
    } else {
      this.selectedIndex = this.selectedIndex <= 0 ? count - 1 : this.selectedIndex - 1;
    }
    this.scrollSelectedIntoView();
  }

  private activateSelected() {
    const cubs = this.navCubResults;
    if (this.selectedIndex < 0 || this.selectedIndex === this.searchMoreIndex) {
      this.navigateToSearch();
      return;
    }
    if (this.selectedIndex < cubs.length) {
      this.itemClick(cubs[this.selectedIndex]);
      return;
    }
    const cat = this.categories[this.selectedIndex - cubs.length];
    if (cat) {
      this.categoryClick(cat);
    }
  }

  // Keeps the highlighted row visible when arrowing through a list taller than the popup.
  private scrollSelectedIntoView() {
    setTimeout(() => {
      const el = document.querySelector('cub-search-result-popup .row-selected') as HTMLElement;
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  }


  // actually have results so the popup never shows a dead "0 results" row.
  private buildCategories(results: UnifiedSearchResult[]) {
    const groupOf = (r: UnifiedSearchResult) => r.group || r.source;
    const propertyFiles = results.filter(r => groupOf(r) === 'Property Files').length;
    const egnyte = results.filter(r => groupOf(r) === 'Egnyte').length;
    const leaseFiles = results.filter(r => groupOf(r) === 'Lease Files').length;
    const sharePoint = results.filter(r => r.source === 'SharePoint' && groupOf(r) !== 'Lease Files').length;

    const egnyteLogo = 'assets/img/egnyte-logo-white.png';
    const spLogo = 'assets/img/sharepoint-logo-white-100x.png';
    const all: CategorySummary[] = [
      { label: 'Property Files', filter: 'Property Files', count: propertyFiles, img: egnyteLogo, colorKey: 'Egnyte' },
      { label: 'Lease Files', filter: 'Lease Files', count: leaseFiles, img: spLogo, colorKey: 'SharePoint' },
      { label: 'Egnyte', filter: 'Egnyte', count: egnyte, img: egnyteLogo, colorKey: 'Egnyte' },
      { label: 'SharePoint', filter: 'SharePoint', count: sharePoint, img: spLogo, colorKey: 'SharePoint' }
    ];
    this.categories = all.filter(c => c.count > 0);
  }

  // Anchors the popup to the toolbar search box so its left edge lines up with the input.
  private positionUnderSearchBox() {
    const box = document.getElementById('searchBox');
    if (!box) {
      return;
    }
    const rect = box.getBoundingClientRect();
    this.posLeft = Math.round(rect.left);
    this.posTop = Math.round(rect.bottom + 6);
    this.posWidth = Math.max(430, Math.round(rect.width));
  }

  hideSearch(): void {
    this.searchService.closeSearch();
  }

  itemClick(item: SearchResult) {
    this.searchService.closeSearch();
    this.navigateCub(item.url);
  }

  // CUB links come in two shapes: relative SPA routes (e.g. "properties/101356/leases/1356fgps")
  // and absolute portal URLs from the table search (e.g. "https://cub.centerpoint.com/properties/101485").
  // Route in-app when the target is this SPA (relative, or absolute on our own origin); otherwise open
  // it in a new tab. Navigating must be absolute so the route isn't resolved relative to the current page.
  private navigateCub(url: string) {
    const raw = (url || '').trim();
    if (!raw) {
      return;
    }
    // Investments come back as a legacy javascript link ("javascript:showEditInv(2563);") — map it
    // to the Angular investment detail route instead of trying to open it as a URL (which lands on
    // a blank tab or trips the router's auxiliary-outlet parser on the parentheses).
    const inv = raw.match(/showEditInv\((\d+)\)/i);
    if (inv) {
      this.router.navigateByUrl('/investments/' + inv[1]);
      return;
    }
    if (/^https?:\/\//i.test(raw)) {
      try {
        const parsed = new URL(raw);
        if (parsed.origin === window.location.origin) {
          this.router.navigateByUrl(parsed.pathname + parsed.search + parsed.hash);
        } else {
          window.open(raw);
        }
      } catch {
        window.open(raw);
      }
      return;
    }
    this.router.navigateByUrl('/' + raw.replace(/^\//, ''));
  }

  // Jumps to the full search page with this category's facet-rail filter pre-selected.
  categoryClick(cat: CategorySummary): void {
    this.searchService.closeSearch();
    const term = this.searchService.lastTerm;
    this.router.navigate(['/search'], {
      queryParams: { ...(term ? { q: term } : {}), filter: cat.filter }
    });
  }

  navigateToSearch() {
    this.searchService.closeSearch();
    const term = this.searchService.lastTerm;
    this.router.navigate(['/search'], { queryParams: term ? { q: term } : {} });
  }

  openFileLink(path: string) {
    window.open(environment.egnyteUrl + path.toLowerCase().replace('/shared/', ''));
  }

  getIcon(filename: string) {
    switch (filename.substring(filename.lastIndexOf('.') + 1)) {
      case 'txt':
        return faFile;
      case 'xls':
      case 'xlsx':
        return faFileExcel;
      case 'doc':
      case 'docx':
        return faFileWord;
      case 'pdf':
        return faFilePdf;
      case 'msg':
        return faEnvelope;
      default:
        return faFile;
    }
  }

}
